import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  linkOptions,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  CircleCheckBig,
  CircleDot,
  CircleMinus,
  FileText,
  PlusCircle,
  Settings,
} from "lucide-react";
import { Suspense, useState } from "react";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import type { FiltersState } from "@/components/data-table-filter/core/types";
import { Page } from "@/components/page";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import { RenderCell } from "@/components/table/cells/render-cell";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserAvatar } from "@/components/user-avatar";
import { usePersistedReactTable } from "@/hooks/use-persisted-table";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import type { RouterOutputs } from "@/lib/trpcClient";

type Request = RouterOutputs["reimbursement"]["list"][number];
type ReimbursementView = "all" | "pending" | "approved" | "rejected" | "draft";

const dtf = createColumnConfigHelper<Request>();

const columnsConfig = [
  // dtf
  //   .text()
  //   .id("Person")
  //   .accessor((row) => row.forUserId)
  //   .displayName("Person")
  //   .icon(User)
  //   .build(),
  dtf
    .text()
    .id("Merchant")
    .accessor((row) => row.reimbursement.merchant)
    .displayName("Merchant")
    .icon(FileText)
    .build(),
  // dtf
  //   .number()
  //   .id("Amount")
  //   .accessor((row) => (row.data.amountCents || 0) / 100)
  //   .displayName("Amount")
  //   .icon(DollarSign)
  //   .build(),
  dtf
    .option()
    .id("Status")
    .accessor((row) => row.request.status)
    .displayName("Status")
    .icon(CircleDot)
    .options([
      { label: "Pending", value: "pending" },
      { label: "Approved", value: "approved" },
      { label: "Rejected", value: "rejected" },
      { label: "Draft", value: "draft" },
    ])
    .hide()
    .build(),
];

const getStatusIcon = (status: string) => {
  switch (status) {
    case "approved":
      return CircleCheckBig;
    case "pending":
      return CircleDot;
    case "draft":
      return CircleMinus;
    case "rejected":
      return AlertCircle;
    default:
      return CircleDot;
  }
};

export const Route = createFileRoute(
  "/_authed/_org/transactions/reimbursements",
)({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(trpc.reimbursement.list.queryOptions());
    queryClient.ensureQueryData(trpc.org.users.queryOptions());
  },
});

const initialFilterState: FiltersState = [
  {
    columnId: "Status",
    hide: true,
    operator: "is not",
    type: "option",
    values: [],
  },
];

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { trpc } = Route.useRouteContext();
  const { admin, bookkeeper } = useRequiredRole();
  const location = useLocation();
  const [view, setView] = useState<ReimbursementView>("all");

  const { data: reimbursementsData, isPending: isReimbursementsLoading } =
    useQuery(trpc.reimbursement.list.queryOptions());
  const reimbursements = reimbursementsData ?? [];

  const { data: users, isPending: isUsersLoading } = useQuery(
    trpc.org.users.queryOptions(),
  );

  const isLoading = isReimbursementsLoading || isUsersLoading;

  const tableColumns: ColumnDef<Request>[] = [
    {
      accessorFn: (row) => users?.userMap[row.request.forUserId || ""]?.name,
      cell: ({ row }) => <UserAvatar userId={row.original.request.forUserId} />,
      id: "User",
    },
    {
      accessorFn: (row) => row.reimbursement.merchant || "",
      cell: ({ row }) => (
        <span className="text-gray-900">
          {row.original.reimbursement.merchant || "No merchant"}
        </span>
      ),
      id: "Merchant",
    },
    {
      accessorFn: (row) => row.reimbursement.amountCents || 0,
      cell: ({ row }) => (
        <RenderCell
          type="currency"
          amount={row.original.reimbursement.amountCents || 0}
        />
      ),
      id: "Amount",
      meta: {
        align: "right",
      },
    },
    {
      accessorFn: (row) => row.reimbursement.date,
      cell: ({ row }) => (
        <RenderCell
          type="date"
          date={
            row.original.reimbursement.date
              ? new Date(row.original.reimbursement.date)
              : new Date()
          }
        />
      ),
      id: "Transaction Date",
    },
    {
      accessorFn: (row) => row.reimbursement.memo || "",
      cell: ({ row }) => (
        <span className="text-gray-700 text-sm">
          {row.original.reimbursement.memo || "No description"}
        </span>
      ),
      id: "Description",
    },
    {
      accessorFn: (row) => row.request.status,
      cell: ({ row }) => {
        const StatusIcon = getStatusIcon(row.original.request.status);
        return (
          <RenderStatusBadge status={row.original.request.status}>
            <StatusIcon className="size-4" />
            <span className="capitalize">{row.original.request.status}</span>
          </RenderStatusBadge>
        );
      },
      id: "Status",
    },
    // {
    //   accessorFn: (row) => row.files?.length || 0,
    //   cell: ({ row }) => (
    //     <span className="text-gray-500 text-sm">
    //       {row.original.files?.length || 0} file(s)
    //     </span>
    //   ),
    //   id: "Files",
    // }
  ];

  const { table, filters, columns, actions, strategy, setTableFilters } =
    usePersistedReactTable({
      columns: tableColumns,
      data: reimbursements ?? [],
      filterOptions: {
        columnsConfig,
        defaultFilters: initialFilterState,
        strategy: "client",
      },
      tableName: "reimbursement-requests",
    });

  const updateStatusFilter = (status: ReimbursementView) => {
    setView(status);
    setTableFilters((filters) =>
      filters.map((f) =>
        f.columnId === "Status"
          ? {
              ...f,
              operator: status === "all" ? "is not" : "is",
              values: status === "all" ? [] : [status],
            }
          : f,
      ),
    );
  };

  const allCount = reimbursements.length;
  const draftCount = reimbursements.filter(
    ({ request: r }) => r.status === "draft",
  ).length;
  const pendingCount = reimbursements.filter(
    ({ request: r }) => r.status === "pending",
  ).length;
  const approvedCount = reimbursements.filter(
    ({ request: r }) => r.status === "approved",
  ).length;
  const rejectedCount = reimbursements.filter(
    ({ request: r }) => r.status === "rejected",
  ).length;

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            replace: true,
            to: "/transactions/reimbursements",
          });
        }
      }}
      open={
        location.pathname !== "/transactions/reimbursements" &&
        location.pathname.includes("/transactions/reimbursements")
      }
    >
      <Page
        heading="Reimbursements"
        actions={
          <>
            {bookkeeper && (
              <Link to="/transactions/reimbursements/settings">
                <Button variant="outline">
                  <Settings />
                  Settings
                </Button>
              </Link>
            )}

            <Link to="/transactions/reimbursements/create">
              <Button>
                <PlusCircle />
                <span className="font-semibold">
                  {admin ? "Create Reimbursement" : "Request Reimbursement"}
                </span>
              </Button>
            </Link>
          </>
        }
        tabs={
          <Tabs
            onValueChange={(v) => {
              updateStatusFilter(v as ReimbursementView);
            }}
            value={view}
          >
            <TabsList variant="standard">
              <TabsTrigger value="all" variant="standard" count={allCount}>
                All
              </TabsTrigger>
              <TabsTrigger value="draft" variant="standard" count={draftCount}>
                Draft
              </TabsTrigger>
              <TabsTrigger
                value="pending"
                variant="standard"
                count={pendingCount}
              >
                Pending
              </TabsTrigger>
              <TabsTrigger
                value="approved"
                variant="standard"
                count={approvedCount}
              >
                Approved
              </TabsTrigger>
              <TabsTrigger
                value="rejected"
                variant="standard"
                count={rejectedCount}
              >
                Rejected
              </TabsTrigger>
            </TabsList>
          </Tabs>
        }
        table={{
          actions,
          columns,
          filters,
          strategy,
          table,
        }}
      >
        <DataTable
          noResultsComponent={
            <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
              <span className="text-xl">No Matching Reimbursements</span>
              <Button onClick={actions?.removeAllFilters} variant={"outline"}>
                Clear Filters
              </Button>
            </div>
          }
          table={table}
          tableName="reimbursement-requests"
          navOptions={(row) =>
            linkOptions({
              params: {
                id: row.original.reimbursement.id,
              },
              to: "/transactions/reimbursements/$id",
            })
          }
          loading={isLoading}
          className="[&_th]:border-t-0!"
        />

        <TablePagination tableName="reimbursements" table={table} />
      </Page>

      <SheetContent
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        size={"large"}
      >
        <Suspense
          fallback={
            <div className="px-6">
              <SheetSkeleton />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </SheetContent>
    </Sheet>
  );
}
